import { IPoint, PointExtensions } from '@foblex/2d';
import { EFConnectableSide, EFConnectionBehavior, EFConnectionType } from '@foblex/flow';
import { generateGuid } from '@foblex/utils';
import { FLOW_NODE_MAP } from '../constants/node-map.constants';
import { FlowNodeType } from '../enums';
import { FlowConnection, FlowNode, FlowState, ViewPort } from '../models';
import { ViewPortType } from '../models/flow-port.model';

export class FlowNodeHelpers {
  static getPosition = (p: IPoint): IPoint => PointExtensions.initialize(p.x, p.y);
  static isRootNode = (nodeType: FlowNodeType): boolean => !!FLOW_NODE_MAP[nodeType]?.root;
  static isGroupNode = (nodeType: FlowNodeType): boolean => nodeType === FlowNodeType.GROUP || nodeType === FlowNodeType.NODE_AS_GROUP;
  static createInput = (params: Partial<ViewPort>): ViewPort => ({
    ...params,
    id: `${params.id}-in`,
    type: ViewPortType.INPUT,
    position: params.position ?? EFConnectableSide.LEFT,
    internal: !!params.internal
  });
  static createOutput = (params: Partial<ViewPort>): ViewPort => ({
    ...params,
    type: ViewPortType.OUTPUT,
    id: `${params.id}-out`,
    position: params.position ?? EFConnectableSide.RIGHT,
    internal: !!params.internal
  });

  static createConnection = (params: Partial<FlowConnection>) =>
    ({
      id: generateGuid(),
      ...params,
      sourcePortId: params.sourcePortId,
      targetPortId: params.targetPortId,
      behavior: params.behavior ?? EFConnectionBehavior.FIXED,
      type: params.type ?? EFConnectionType.ADAPTIVE_CURVE
    }) as FlowConnection;

  static findChildren = (node: FlowNode, nodes: FlowNode[]): FlowNode[] => nodes.filter((n) => n.parentId === node?.id);

  static findConnectionsUsedInNode = (node: FlowNode, connections: FlowConnection[]): FlowConnection[] => {
    const inputIds = node?.ports?.filter((port: ViewPort) => port.type === ViewPortType.INPUT).map((port: ViewPort) => port.id) ?? [];
    const outputIds = node?.ports?.filter((port: ViewPort) => port.type === ViewPortType.OUTPUT).map((port: ViewPort) => port.id) ?? [];
    const connectorIds = new Set([...inputIds, ...outputIds]);

    return connections.filter((connection) => connectorIds.has(connection.sourcePortId) || connectorIds.has(connection.targetPortId));
  };

  static createNode = (node: Partial<FlowNode>, connectorSides: { type: ViewPortType; position: EFConnectableSide; cssClassName?: string }[]): FlowNode => {
    const ports = [];
    for (const item of connectorSides) {
      if (item?.type === ViewPortType.INPUT) {
        ports.push(FlowNodeHelpers.createInput({ id: node.id, position: item.position, cssClassName: item?.cssClassName }));
      } else if (item?.type === ViewPortType.OUTPUT) {
        ports.push(FlowNodeHelpers.createOutput({ id: node.id, position: item.position, cssClassName: item?.cssClassName }));
      }
    }
    return {
      ...node,
      ports
    } as FlowNode;
  };

  static setConnectionCssClasses = (selectedConnections: FlowConnection[], state: FlowState, cssClassName: string): Record<string, FlowConnection> => {
    const connections = Object.values(state?.connections);
    return connections.reduce<Record<string, FlowConnection>>((acc, conn) => {
      const found = selectedConnections.find((c) => c.id === conn.id);
      if (found && !conn?.cssClassName?.includes(cssClassName)) {
        conn.cssClassName = `${conn?.cssClassName ?? ''} ${cssClassName}`;
      } else if (!found) {
        conn.cssClassName = conn?.cssClassName?.replace(cssClassName, '');
      }
      return {
        ...acc,
        [conn.id]: { ...conn }
      };
    }, {});
  };

  static removeConnectionCssClasses = (connections: FlowConnection[], cssClassName: string): Record<string, FlowConnection> =>
    connections.reduce<Record<string, FlowConnection>>((acc, conn) => {
      conn.cssClassName = conn?.cssClassName?.replace(cssClassName, '');
      return {
        ...acc,
        [conn.id]: { ...conn }
      };
    }, {});

  static getSelectedNodesWithChildren = (selectedNodesIds: string[], state: FlowState): FlowNode[] => {
    const nodes: FlowNode[] = [];
    for (const nodeId of selectedNodesIds) {
      const node = state.nodes[nodeId];
      nodes.push(node);
      if (node.type === FlowNodeType.NODE_AS_GROUP || node.type === FlowNodeType.GROUP) {
        const childrenIds = FlowNodeHelpers.findChildren(node, Object.values(state.nodes)).map((x) => x.id);
        for (const childrenId of childrenIds) {
          nodes.push(state.nodes[childrenId]);
        }
      }
    }
    return nodes;
  };

  static getConnectionsFromNodesByPorts = (nodes: FlowNode[], state: FlowState): FlowConnection[] => {
    if (!nodes.length) {
      return [];
    }

    const allConnections = Object.values(state.connections);
    const ports = nodes.reduce((acc: ViewPort[], node: FlowNode) => [...acc, ...(node?.ports ?? [])], []);

    return ports.reduce((acc: FlowConnection[], port) => {
      const found = allConnections.filter(
        (connection) =>
          (connection.sourcePortId === port.id && nodes.find((n) => n.id === connection.sourceNodeId)) ||
          (connection.targetPortId === port.id && nodes.find((n) => n.id === connection.targetNodeId))
      );
      return found ? [...acc, ...found] : [...acc];
    }, [] as FlowConnection[]);
  };
}
